// src/pages/PrincipalRegistration.js (v2 – config-driven, clean)

import React, { useState, useEffect } from "react";

import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/PrincipalRegistration.css";

/**
 * Principal registration page.
 *
 * Looks up school by Roll Number, collects email, website, and class scheme,
 * then registers the principal account.
 *
 * @component
 * @returns {JSX.Element}
 */
const PrincipalRegistration = () => {
  const [rollNumber, setRollNumber] = useState("");
  const [schoolData, setSchoolData] = useState(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);

  const [schoolWebsite, setSchoolWebsite] = useState("");

  const [countryCode, setCountryCode] = useState("IE");
  const [classSchemeKey, setClassSchemeKey] = useState("IE_PRIMARY");
  const [schoolYearStartMonth, setSchoolYearStartMonth] = useState("9");

  const countryOptions = [
    { value: "IE", label: "Ireland" },
    { value: "UK", label: "United Kingdom" },
    { value: "US", label: "United States" },
  ];

  const schemeOptionsByCountry = {
    IE: [
      { value: "IE_PRIMARY", label: "Primary (Juniors–6th Class)" },
      { value: "IE_SECONDARY", label: "Secondary (1st–6th Year)" },
    ],
    UK: [
      { value: "UK_PRIMARY", label: "Primary (Reception–Year 6)" },
      { value: "UK_SECONDARY", label: "Secondary (Year 7–13)" },
    ],
    US: [
      { value: "US_ELEMENTARY", label: "Elementary (K–5)" },
      { value: "US_MIDDLE", label: "Middle (6–8)" },
      { value: "US_HIGH", label: "High School (9–12)" },
    ],
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: new Date(0, i).toLocaleString("en", { month: "long" }),
  }));

  const resolveDefaultSchemeForCountry = (cc) => {
    const defaults = {
      IE: "IE_PRIMARY",
      UK: "UK_PRIMARY",
      US: "US_ELEMENTARY",
    };
    return defaults[cc] || "IE_PRIMARY";
  };

  // Scroll to top when message changes
  useEffect(() => {
    if (message) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [message]);

  const fetchSchoolData = async (cleanedRollNumber) => {
    if (!cleanedRollNumber) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setMessage("");
      return;
    }

    try {
      const { data } = await api.get(API_PATHS.GET_SCHOOL_DATA(cleanedRollNumber));
      setSchoolData(data);
      setPrincipalEmail(data.principal_email || "");
      setEmail(data.principal_email || "");
      setSchoolWebsite(data.school_website || "");

      const detectedCountry = data.country_code || "IE";
      setCountryCode(detectedCountry);
      setClassSchemeKey(data.class_scheme_key || resolveDefaultSchemeForCountry(detectedCountry));
      setSchoolYearStartMonth(String(data.school_year_start_month || 9));

      setMessage("");
    } catch (error) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setCountryCode("IE");
      setClassSchemeKey("IE_PRIMARY");
      setSchoolYearStartMonth("9");
      setMessage("School not found. Please check the Roll Number.");
    }
  };

  const handleRegister = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage("Please enter a valid email address.");
      return;
    }

    if (!schoolWebsite.trim()) {
      setMessage("Please enter your school's website URL.");
      return;
    }

    const validSchemes = schemeOptionsByCountry[countryCode]?.map((s) => s.value) || [];
    const schemeToSend = validSchemes.includes(classSchemeKey)
      ? classSchemeKey
      : resolveDefaultSchemeForCountry(countryCode);

    const payload = {
      email: email.trim(),
      role: "PRINCIPAL",
      roll_number: rollNumber.trim(),
      school_website: schoolWebsite.trim(),
      country_code: countryCode,
      class_scheme_key: schemeToSend,
      school_year_start_month: parseInt(schoolYearStartMonth, 10) || 9,
    };

    try {
      const { data } = await api.post(API_PATHS.PRINCIPAL_REGISTRATION, payload);
      setMessage(data.message || "Registration successful! Check your email.");
      setIsRegistered(true);
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Registration failed. Please try again."
      );
    }
  };

  const currentSchemeOptions = schemeOptionsByCountry[countryCode] || [];

  return (
    <div className="principal-registration page">
      <div className="registration-card">
        <header className="registration-header">
          <h1 className="registration-title">Principal Registration</h1>
          <p className="registration-subtitle">
            Start your free 7-day trial — no card required. We’ll match your school by Roll Number and send your login link by email.
          </p>
        </header>

        {message && (
          <p
            className={`message ${message.includes("failed") || message.includes("not found") ? "error" : "success"}`}
            aria-live="polite"
          >
            {message}
          </p>
        )}

        {/* Step 1: Find school */}
        <section className="registration-section">
          <h2 className="section-title">1. Find your school</h2>
          <div className="form-group">
            <label htmlFor="roll-number">Roll Number</label>
            <input
              id="roll-number"
              type="text"
              className="input-field"
              placeholder="Enter your school’s Roll Number"
              value={rollNumber}
              onChange={(e) => {
                const cleaned = e.target.value.trim();
                setRollNumber(cleaned);
                if (cleaned.length >= 5) fetchSchoolData(cleaned);
              }}
              onBlur={(e) => setRollNumber(e.target.value.trim())}
            />
          </div>

          {schoolData && (
            <div className="school-details">
              <p><strong>School Name:</strong> {schoolData.official_name}</p>
              <p><strong>Address:</strong> {schoolData.address_line_1}, {schoolData.county_description}</p>
              <p><strong>Eircode:</strong> {schoolData.eircode}</p>
              <p><strong>Phone:</strong> {schoolData.phone_no}</p>
              <p><strong>Principal:</strong> {schoolData.principal_name}</p>
              <p><strong>Principal Email:</strong> {principalEmail}</p>
              <p><strong>Enrolment:</strong> {schoolData.enrolment_quantity}</p>
              {schoolData.school_website && (
                <p>
                  <strong>Website on file:</strong>{" "}
                  <a href={schoolData.school_website} target="_blank" rel="noopener noreferrer">
                    {schoolData.school_website}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="school-website">
              School website <span className="required">*</span>
            </label>
            <input
              id="school-website"
              type="url"
              className="input-field"
              placeholder="https://www.yourschool.ie"
              value={schoolWebsite}
              onChange={(e) => setSchoolWebsite(e.target.value)}
              required
            />
            <span className="tooltip">
              Required — helps us verify your school and connect the WordPress plugin.
            </span>
          </div>
        </section>

        {/* Step 2: Email */}
        <section className="registration-section">
          <h2 className="section-title">2. Confirm your email</h2>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="input-field"
              placeholder="Your principal email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </section>

        {/* Step 3: Location & year groups */}
        <section className="registration-section">
          <h2 className="section-title">3. School location & year groups</h2>
          <p className="section-helper">
            This helps us show the right class/year options for your teachers.
          </p>

          <div className="form-group">
            <label htmlFor="country-code">Country</label>
            <select
              id="country-code"
              className="input-field"
              value={countryCode}
              onChange={(e) => {
                const next = e.target.value;
                setCountryCode(next);
                setClassSchemeKey(resolveDefaultSchemeForCountry(next));
              }}
            >
              {countryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="class-scheme">School type / year groups</label>
            <select
              id="class-scheme"
              className="input-field"
              value={classSchemeKey}
              onChange={(e) => setClassSchemeKey(e.target.value)}
            >
              {currentSchemeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="year-start">School year starts in</label>
            <select
              id="year-start"
              className="input-field"
              value={schoolYearStartMonth}
              onChange={(e) => setSchoolYearStartMonth(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          className="register-button"
          onClick={handleRegister}
          disabled={
            !rollNumber ||
            !email ||
            !schoolData ||
            !schoolWebsite.trim() ||
            isRegistered
          }
        >
          {isRegistered ? "Registered ✅" : "Register"}
        </button>
      </div>
    </div>
  );
};

export default PrincipalRegistration;